import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { usePortalAuth } from "../lib/portalAuth";
import { formatApiErrorDetail } from "../lib/operatorWalk";
import PortalAuthShell from "../components/ow/PortalAuthShell";

export default function PortalLogin() {
  const { user, login } = usePortalAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/portal"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u.role === "admin" ? "/admin" : "/portal", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PortalAuthShell
      title="Client login"
      subtitle="Sign in to view your monthly operator reports and trends."
      footer={
        <span>
          Not a client yet?{" "}
          <Link to="/services/operator-walk" className="text-brand-700 underline" data-testid="portal-learn-more-link">
            Learn about The Operator Walk
          </Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} className="space-y-5" data-testid="portal-login-form">
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full border border-brand-200 px-4 py-3 text-sm focus:outline-none focus:border-brand-500"
            data-testid="portal-login-email"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 w-full border border-brand-200 px-4 py-3 text-sm focus:outline-none focus:border-brand-500"
            data-testid="portal-login-password"
            autoComplete="current-password"
          />
        </div>
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="portal-login-error">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-6 py-3.5 text-sm font-medium transition-colors disabled:opacity-60"
          data-testid="portal-login-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Sign in
        </button>
        <div className="text-center">
          <Link to="/portal/forgot" className="text-sm text-brand-700 hover:underline" data-testid="portal-forgot-link">
            Forgot your password?
          </Link>
        </div>
      </form>
    </PortalAuthShell>
  );
}
